import { Box, Stack } from "@mui/material";
import { Signing } from "pandadoc-signing";
import { useLayoutEffect, useRef } from "react";

import { APP_V2_APP_EVENTS, logError } from "../../../../../lib/analytics";

interface PandaDocEmbeddedSigningProps {
  pandaDocSessionId: string;
  onDocumentLoaded: () => void;
  onDocumentRendered: () => void;
  onError: () => void;
  onComplete: () => void;
}

const PANDADOC_CONTAINER_ID_PREFIX = "pandadoc-signing-container";

export function PandaDocEmbeddedSigning(props: PandaDocEmbeddedSigningProps) {
  const { pandaDocSessionId, onDocumentLoaded, onDocumentRendered, onError, onComplete } = props;
  const iframeContainerId = `${PANDADOC_CONTAINER_ID_PREFIX}-${pandaDocSessionId}`;
  const signingRef = useRef<Signing | undefined>(undefined);

  /*
   Use useLayoutEffect because new Signing(iframeContainerId...)
   wants DOM for iframeContainerId present.
   Since useLayoutEffect runs after react commits DOM useLayoutEffect runs
   */
  useLayoutEffect(() => {
    const signing = new Signing(iframeContainerId, {
      width: "100%",
      height: "100%",
    });

    signingRef.current = signing;
    void signing.open({ sessionId: pandaDocSessionId });
    return () => {
      signing.off("document.completed");
      signing.off("document.exception");
      signing.off("document.loading.error");
      signing.off("document.loaded");
      signing.off("document.rendered");
      signing.destroy();
    };
  }, [iframeContainerId, pandaDocSessionId]);

  /*
   Event Listeners, using separate useLayoutEffect
   to avoid destroying and recreating the signing instance
   if event handler props change
   */
  useLayoutEffect(() => {
    if (!signingRef.current) {
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error: new Error("Signing instance not found"),
      });
      return;
    }
    const signingInstance = signingRef.current;
    signingInstance?.on("document.completed", onComplete);
    signingInstance?.on("document.exception", onError);
    signingInstance?.on("document.loading.error", onError);
    signingInstance?.on("document.loaded", onDocumentLoaded);
    signingInstance?.on("document.rendered", onDocumentRendered);
    return () => {
      signingInstance?.off("document.completed");
      signingInstance?.off("document.exception");
      signingInstance?.off("document.loading.error");
      signingInstance?.off("document.loaded");
      signingInstance?.off("document.rendered");
    };
  }, [onComplete, onError, onDocumentLoaded, onDocumentRendered]);

  return (
    <Stack
      sx={{
        height: "100%",
        width: "100%",
        "& iframe": {
          height: "100%",
          width: "100%",
        },
      }}
    >
      <Box id={iframeContainerId} />
    </Stack>
  );
}
